import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, FileText } from 'lucide-react';
import { catalogService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { CatalogItem } from '../types/catalog';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import CatalogList from '../components/catalog/CatalogList';
import CatalogForm from '../components/catalog/CatalogForm';
import FileUploader from '../components/catalog/FileUploader';
import Modal from '../components/ui/Modal';

type ModalType = 'create' | 'edit' | 'upload' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

function Catalog() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingItem, setEditingItem] = useState<CatalogItem | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string>();
  const queryClient = useQueryClient();

  // Queries
  const { data: catalogData } = useQuery({
    queryKey: ['catalog'],
    queryFn: () => catalogService.getItems(),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!selectedFile) {
        throw new Error('É necessário selecionar um arquivo');
      }

      // First create the catalog item
      const item = await catalogService.createItem(data);

      // Then upload the file
      await catalogService.uploadFile(item.id, selectedFile);

      return item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
      setSelectedFile(null);
      messageHandler.success('Item criado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => catalogService.updateItem(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
      messageHandler.success('Item atualizado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => catalogService.deleteItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
      messageHandler.success('Item excluído com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({ itemId, file }: { itemId: number; file: File }) =>
      catalogService.uploadFile(itemId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
      messageHandler.success('Arquivo enviado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteFileMutation = useMutation({
    mutationFn: ({ itemId, fileId }: { itemId: number; fileId: number }) =>
      catalogService.deleteFile(itemId, fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
      messageHandler.success('Arquivo excluído com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  // Handlers
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setUploadError(undefined);

    const formData = new FormData(e.currentTarget);

    const data = {
      type: formData.get('type') as string,
      title: formData.get('title') as string,
      summary: formData.get('summary') as string,
      specifications: {
        version: formData.get('version'),
        language: formData.get('language'),
      },
    };

    try {
      if (editingItem) {
        updateMutation.mutate({ id: editingItem.id, data });
      } else {
        createMutation.mutate(data);
      }
    } catch (error: any) {
      setUploadError(error.message);
    }
  };

  const handleDelete = (item: CatalogItem) => {
    messageHandler.confirm('Excluir Item', 'Tem certeza que deseja excluir este item?', () =>
      deleteMutation.mutate(item.id)
    );
  };

  const handleDeleteFile = (itemId: number, fileId: number, fileCount: number) => {
    if (fileCount <= 1) {
      messageHandler.error(
        'Não é possível excluir o último arquivo. O item deve ter pelo menos um arquivo.'
      );
      return;
    }

    messageHandler.confirm('Excluir Arquivo', 'Tem certeza que deseja excluir este arquivo?', () =>
      deleteFileMutation.mutate({ itemId, fileId })
    );
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingItem(null);
    setSelectedFile(null);
    setUploadError(undefined);
  };

  const items = catalogData?.data || [];

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Catálogo"
          description="Gerencie seus arquivos e documentos."
          actions={
            <Button
              onClick={() => {
                setEditingItem(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Item
            </Button>
          }
        />

        {items.length > 0 ? (
          <CatalogList
            items={items}
            onEdit={item => {
              setEditingItem(item);
              setModal({ type: 'edit' });
            }}
            onDelete={handleDelete}
            onUpload={item => {
              setEditingItem(item);
              setModal({ type: 'upload' });
            }}
            onDeleteFile={handleDeleteFile}
          />
        ) : (
          <EmptyState
            icon={<FileText className="h-12 w-12" />}
            title="Nenhum item no catálogo"
            description="Crie seu primeiro item no catálogo para começar"
            action={{
              label: 'Criar Item',
              onClick: () => {
                setEditingItem(null);
                setModal({ type: 'create' });
              },
            }}
          />
        )}

        {/* Create/Edit Modal */}
        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingItem ? 'Editar Item' : 'Novo Item'}
        >
          <CatalogForm
            item={editingItem}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
            onFileSelect={setSelectedFile}
            selectedFile={selectedFile}
            uploadError={uploadError}
          />
        </Modal>

        {/* Upload Modal */}
        <Modal
          isOpen={modal.type === 'upload'}
          onClose={closeModal}
          title={`Upload de Arquivo - ${editingItem?.title}`}
        >
          <FileUploader
            onDrop={files => {
              if (editingItem && files.length > 0) {
                uploadMutation.mutate({
                  itemId: editingItem.id,
                  file: files[0],
                });
              }
            }}
            isLoading={uploadMutation.isPending}
          />
        </Modal>
      </div>
    </div>
  );
}

export default Catalog;
